// apps/web/src/components/reports/ReportsManager.tsx
// Reports management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Report } from '../../types/report';
import { apiClient } from '../../lib/apiClient';

export function ReportsManager() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/reports');
        setReports(response.data.reports);
      } catch (err) {
        setError('Failed to load reports');
        console.error('Error loading reports:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  const handleCreateReport = async (reportData: any) => {
    try {
      const response = await apiClient.post('/api/v1/reports', reportData);
      setReports([response.data, ...reports]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create report');
      console.error('Error creating report:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="reports-manager">
      <div className="header">
        <h2>Reports Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Report'}
        </button>
      </div>

      {showCreateForm && (
        <ReportForm 
          onSubmit={handleCreateReport} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="reports-grid">
        {reports.map(report => (
          <ReportCard key={report.id} report={report} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function ReportCard({ report }: { report: Report }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'status-active';
      case 'INACTIVE': return 'status-inactive';
      case 'ARCHIVED': return 'status-archived';
      case 'GENERATING': return 'status-generating';
      default: return 'status-default';
    }
  };

  return (
    <div className="report-card">
      <div className="report-header">
        <h3>{report.name}</h3>
        <span className={`status-badge ${getStatusColor(report.status)}`}>
          {report.status}
        </span>
      </div>
      <p>{report.description || 'No description'}</p>
      <p><strong>Type:</strong> {report.reportType}</p>
      <p><strong>Created:</strong> {new Date(report.createdAt).toLocaleDateString()}</p>
      <p><strong>Last Generated:</strong> {report.lastGeneratedAt ? new Date(report.lastGeneratedAt).toLocaleDateString() : 'Never'}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-primary">Generate</button>
        <button className="btn btn-outline">Download</button>
      </div>
    </div>
  );
}

// Helper component
function ReportForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reportType: 'FINANCIAL',
    frequency: 'MONTHLY',
    recipients: [] as string[],
    filters: {}
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="report-form">
      <h3>Create New Report</h3>
      <input
        type="text"
        placeholder="Report Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.reportType}
        onChange={(e) => setFormData({...formData, reportType: e.target.value})}
      >
        <option value="FINANCIAL">Financial</option>
        <option value="MARKETING">Marketing</option>
        <option value="CONTENT">Content</option>
        <option value="CREATOR">Creator</option>
        <option value="CAMPAIGN">Campaign</option>
        <option value="ANALYTICS">Analytics</option>
      </select>
      <select
        value={formData.frequency}
        onChange={(e) => setFormData({...formData, frequency: e.target.value})}
      >
        <option value="DAILY">Daily</option>
        <option value="WEEKLY">Weekly</option>
        <option value="MONTHLY">Monthly</option>
        <option value="QUARTERLY">Quarterly</option>
        <option value="YEARLY">Yearly</option>
      </select>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Report</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}